import { DomainForm } from "@/components/domain";
import { DraggableList } from "@/components/draggable";
import { SelectSearch } from "@/components/select-search";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetServiceProvider,
  useGetServiceProviders,
} from "@/hooks/service-providers/get.hooks";
import { useGetService, useGetServices } from "@/hooks/services";
import { T } from "@/i18n";
import { CreateTourService, TourService } from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { useTourDaysContext } from "./context";

export function Services({
  day,
  services,
  tourId,
}: {
  services: TourService[];
  day: number;
  tourId: number;
}) {
  const { reorderServices, addNewService } = useTourDaysContext();
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          <T tKey="tourDays.service.title" />
        </span>
        <UpsertCard
          day={day}
          order={services.length + 1}
          tourId={tourId}
          onUpsert={async (model) => {
            addNewService(day, model);
          }}
        >
          <Button size="sm" variant="text" className="m-1">
            <Plus />
            <T tKey="tourDays.service.new" />
          </Button>
        </UpsertCard>
      </div>
      <div className="grid gap-2">
        <DraggableList
          list={services}
          getId={(s) => s.id}
          onChange={(v) =>
            reorderServices(
              day,
              v.map((s) => s.order),
            )
          }
          render={(elem) => <ServiceCard tourService={elem} key={elem.id} />}
        ></DraggableList>
      </div>
    </div>
  );
}

function ServiceCard({ tourService }: { tourService: TourService }) {
  const { updateService, deleteService } = useTourDaysContext();
  const { data: service, isLoading: serviceLoading } = useGetService(
    tourService.serviceId.toString(),
  );
  const { data: serviceProvider, isLoading: serviceProviderLoading } =
    useGetServiceProvider(
      "service",
      tourService.serviceProviderId?.toString() ?? "",
      { enabled: typeof tourService.serviceProviderId === "number" },
    );
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: tourService.id });

  const hour = useMemo(() => {
    if (tourService.startHour && tourService.endHour) {
      return `${tourService.startHour} - ${tourService.endHour}`;
    }
    if (tourService.startHour) {
      return tourService.startHour;
    }
    if (tourService.endHour) {
      return tourService.endHour;
    }
    return undefined;
  }, [tourService.endHour, tourService.startHour]);
  return (
    <div
      className="flex items-start gap-2"
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
    >
      <Button
        type="button"
        className="mt-2 cursor-grab"
        variant="ghost"
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </Button>
      <Card className="w-full">
        <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
          <div className="grid gap-1">
            {serviceLoading || !service ? (
              <Skeleton className="h-5 w-1/5" />
            ) : (
              <span className="font-semibold">{service.name}</span>
            )}
            <span className="text-primary">{hour ?? "-"}</span>
          </div>
          <div>
            <UpsertCard
              day={tourService.day}
              order={tourService.order}
              tourId={tourService.tourId}
              model={tourService}
              onUpsert={async (model) => {
                updateService(tourService.day, {
                  id: tourService.id,
                  ...model,
                });
              }}
            >
              <Button variant="ghost" size="icon" className="m-1">
                <Pencil />
                <span className="sr-only">
                  <T tKey="tourDays.service.edit" />
                </span>
              </Button>
            </UpsertCard>
            <Button
              variant="text-destructive"
              size="icon"
              className="m-1"
              onClick={() => deleteService(tourService.day, tourService.id)}
            >
              <Trash />
              <span className="sr-only">
                <T tKey="tourDays.service.delete" />
              </span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <span className="break-all pl-1 text-muted-foreground">
            <span className="font-medium text-card-foreground">
              <T tKey="tourDays.service.provider" />
            </span>
            {serviceProviderLoading || !serviceProvider ? (
              <Skeleton className="h-5 w-1/5" />
            ) : (
              (serviceProvider.provider?.name ?? "-")
            )}
          </span>
        </CardContent>
      </Card>
    </div>
  );
}

function UpsertCard({
  day,
  order,
  tourId,
  onUpsert,
  model,
  children,
}: {
  day: number;
  tourId: number;
  order: number;
  model?: CreateTourService;
  onUpsert: (v: CreateTourService) => Promise<void>;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <Form
          model={model}
          onUpsert={async (v) => {
            await onUpsert(v);
            setOpen(false);
          }}
          day={day}
          order={order}
          tourId={tourId}
        />
      </PopoverContent>
    </Popover>
  );
}

function Form({
  day,
  order,
  tourId,
  onUpsert,
  model,
}: {
  day: number;
  tourId: number;
  order: number;
  model?: CreateTourService;
  onUpsert: (v: CreateTourService) => Promise<void>;
}) {
  const { data: services, isLoading: servicesLoading } = useGetServices();
  const form = useForm<CreateTourService>({
    day: {
      value: model?.day ?? day,
    },
    tourId: {
      value: model?.tourId ?? tourId,
    },
    order: {
      value: model?.order ?? order,
    },
    endHour: {
      value: model?.endHour ?? "",
    },
    startHour: {
      value: model?.startHour ?? "",
    },
    serviceId: {
      value: model?.serviceId ?? -1,
      validator: z.number().min(0, "errors.required"),
    },
    serviceProviderId: {
      value: model?.serviceProviderId,
    },
  });

  return (
    <DomainForm
      form={form}
      onUpsert={onUpsert}
      buttonTranslation={
        model ? "tourDays.service.update" : "tourDays.service.create"
      }
      fields={[
        {
          key: "startHour",
          label: "tourDays.service.fields.startHour",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.startHour ?? ""}
              type="time"
              onChange={(e) => {
                form.setValue("startHour", e.target.value);
              }}
            />
          ),
        },
        {
          key: "endHour",
          label: "tourDays.service.fields.endHour",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.endHour ?? ""}
              type="time"
              onChange={(e) => {
                form.setValue("endHour", e.target.value);
              }}
            />
          ),
        },
        {
          key: "serviceId",
          label: "tourDays.service.fields.serviceId",
          input: (form, id) =>
            servicesLoading || !services ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.serviceId ?? undefined}
                onChange={(v) => {
                  form.setValue("serviceId", v);
                }}
                options={services.map((s) => ({ label: s.name, value: s.id }))}
              />
            ),
        },
        {
          key: "serviceProviderId",
          label: "tourDays.service.fields.serviceProviderId",
          input: (form, id) => (
            <ServiceProviderSearch
              id={id}
              serviceProviderId={form.value.serviceProviderId?.toString()}
              serviceId={form.value.serviceId}
              onChange={(v) => {
                form.setValue("serviceProviderId", Number.parseInt(v));
              }}
            />
          ),
        },
      ]}
    />
  );
}

function ServiceProviderSearch({
  id,
  serviceId,
  serviceProviderId,
  onChange,
}: {
  id: string;
  serviceProviderId: string | null | undefined;
  serviceId: number;
  onChange: (v: string) => void;
}) {
  const { data: serviceProviders, isLoading: serviceProvidersLoading } =
    useGetServiceProviders("service", serviceId.toString(), {
      enabled: serviceId >= 0,
    });

  if (serviceProvidersLoading || !serviceProviders) {
    return <Skeleton className="h-9 w-full" />;
  }

  return (
    <SelectSearch
      id={id}
      className="w-full"
      value={serviceProviderId ?? undefined}
      onChange={onChange}
      options={serviceProviders.map((s) => ({
        label: s.provider?.name ?? "-",
        value: s.id,
      }))}
    />
  );
}
